import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { toggleLike } from "../../api/community";
import {
  ThumbsUp,
  MessageSquare,
  Share2,
  Bookmark,
  CheckCircle2,
  MoreHorizontal,
  Building2,
} from "lucide-react";

function CardComponent({ post, onPostUpdate }) {
  const userData = localStorage.getItem("user");
  const savedUser = userData ? JSON.parse(userData) : null;
  const currentUserId = savedUser?._id;

  const [isLiked, setIsLiked] = useState(
    post.like?.includes(currentUserId) || post.isLikedByMe || false
  );
  const [likeCount, setLikeCount] = useState(post.like?.length || post.likeCount || 128);
  const [isSaved, setIsSaved] = useState(false);
  const commentCount = post.comment?.length || post.commentCount || 36;

  const handleLikeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const originalIsLiked = isLiked;
    const originalLikeCount = likeCount;

    setIsLiked(!isLiked);
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));

    const token = localStorage.getItem("token");
    if (token && post._id && !post._id.startsWith("post_")) {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        await toggleLike(post._id, config);
      } catch (err) {
        setIsLiked(originalIsLiked);
        setLikeCount(originalLikeCount);
        toast.error("Failed to update like");
      }
    }
  };

  const handleBookmarkToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSaved(!isSaved);
    toast.info(isSaved ? "Removed from saved posts" : "Saved post to bookmarks!");
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Post link copied to clipboard!");
    }
  };

  const authorName = post.owner?.name || post.authorName || "Aditya Rai";
  const authorAvatar =
    post.owner?.avatar ||
    post.authorAvatar ||
    "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80";
  const collageName =
    post.collage || post.owner?.collage || "Bansal Institute of Engineering & Tech";
  const timeAgo = post.timeAgo || "2h ago";
  const scopeTag = post.scope || (post.isPublic ? "Public Post" : "College Community");
  const isVerified = post.owner?.verified !== false;
  const tags = post.tags || ["Placement", "2026 Batch", "Important"];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100/90 overflow-hidden hover:shadow-md transition-all duration-300">
      <Link to={`/community/openPost/${post._id}`} className="block">
        {/* Header section */}
        <div className="p-5 pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <img
                src={authorAvatar}
                alt={authorName}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-100"
              />
              <div>
                <div className="flex items-center space-x-1.5">
                  <h4 className="font-bold text-gray-900 text-sm">{authorName}</h4>
                  {isVerified && (
                    <CheckCircle2
                      size={15}
                      className="text-blue-500 fill-blue-500 text-white shrink-0"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-500 font-medium">
                  {collageName} • {timeAgo}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  scopeTag === "Public Post"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                    : "bg-purple-50 text-purple-700 border border-purple-100"
                }`}
              >
                {scopeTag}
              </span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MoreHorizontal size={18} />
              </button>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-base font-bold text-gray-900 mt-3 mb-1.5 leading-snug group-hover:text-purple-600 transition-colors">
            {post.title}
          </h3>

          {/* Description */}
          {post.description && (
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-3">
              {post.description}
            </p>
          )}
        </div>

        {/* Media / Custom Banner Section */}
        {post.image?.url ? (
          <div className="relative w-full h-56 bg-gray-100 overflow-hidden">
            <img
              src={post.image.url}
              alt={post.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        ) : post.bannerStyle === "placement" || !post.image ? (
          <div className="relative w-full h-44 px-6 py-5 bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 text-white flex items-center justify-between overflow-hidden">
            {/* Background geometric accents */}
            <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-blue-600/10 clip-path-polygon opacity-60"></div>
            <div className="absolute -left-10 -bottom-10 w-36 h-36 bg-purple-500/20 rounded-full blur-2xl"></div>

            <div className="relative z-10 max-w-[65%]">
              <span className="text-[10px] font-bold tracking-widest text-blue-300 uppercase block mb-1">
                Campus Drive 2026
              </span>
              <h2 className="text-xl font-extrabold tracking-tight text-white leading-tight">
                CAMPUS PLACEMENT DRIVE 2026
              </h2>
              <p className="text-xs text-blue-200 mt-1 font-medium">
                YOUR FUTURE STARTS HERE!
              </p>

              <button className="mt-3 px-4 py-1.5 bg-white text-indigo-950 font-extrabold text-xs rounded-full shadow-md hover:bg-blue-50 transition-colors">
                Stay Tuned
              </button>
            </div>

            {/* Illustration Graphic Accent */}
            <div className="relative z-10 hidden sm:flex items-center justify-center w-28 h-28 bg-gradient-to-br from-blue-500/30 to-purple-500/30 backdrop-blur-sm rounded-2xl border border-white/10 p-3 shadow-inner">
              <div className="text-center">
                <Building2 className="w-10 h-10 mx-auto text-blue-300 mb-1" />
                <span className="text-[10px] font-bold text-white block">
                  TOP RECRUITERS
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Tags */}
        <div className="p-5 pt-3 pb-3">
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50/80 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Action buttons footer bar */}
          <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLikeClick}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  isLiked
                    ? "bg-purple-100 text-purple-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ThumbsUp
                  size={15}
                  className={isLiked ? "fill-purple-700 text-purple-700" : ""}
                />
                <span>{likeCount}</span>
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-all"
              >
                <MessageSquare size={15} />
                <span>{commentCount}</span>
              </button>

              <button
                onClick={handleShare}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-all"
              >
                <Share2 size={15} />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>

            <button
              onClick={handleBookmarkToggle}
              className={`p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-all ${
                isSaved ? "text-purple-600 fill-purple-600" : ""
              }`}
            >
              <Bookmark
                size={16}
                className={isSaved ? "fill-purple-600 text-purple-600" : ""}
              />
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default CardComponent;
